import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import ProductFeatureSection from "../common/product-feature";
import '../styles/_features-section.module.scss';

type FeatureButton = {
  text: string;
  link: string;
};

type ProductFeature = {
    id: string;
    image: string;
    imageAlt: string;
    heading: string;
    title: string;
    list: string[];
    buttonTxt?: FeatureButton[];
    exploreBtn?: string;
    exploreLink?: string;
    docsLink?: string;
    docsLabel?: string;
};

type Props = {
    productFeatures: ProductFeature[];
    title: React.ReactNode;
    tourId: string,
    className?: string,
    description?: string,
    isTryOut?: boolean,
    hasActionsDemo?: boolean,
}

const FeaturesMonitoring: React.FC<Props> = ({productFeatures, title, tourId, className, description, isTryOut, hasActionsDemo}) => {
    const { t } = useTranslation('common');
    const [activeTab, setActiveTab] = useState(null);
    const sections = useRef([]);
    const featuresSectionRef = useRef(null);
    
    // Ensure productFeatures is always an array
    const featuresArray = Array.isArray(productFeatures) ? productFeatures : []

    const handleScroll = () => {
        if (!featuresSectionRef.current) return;

        const scrollPosition = window.scrollY + window.innerHeight / 2;
        const sectionTop = featuresSectionRef.current.offsetTop;
        const sectionBottom = sectionTop + featuresSectionRef.current.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
            sections.current.forEach((section, index) => {
                if (section.offsetTop + sectionTop <= scrollPosition && section.offsetTop + sectionTop + section.offsetHeight > scrollPosition) {
                    setActiveTab(index);
                }
            });
        }
    };

    const handleClick = (index) => {
        setActiveTab(index);
        sections.current[index].scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <section ref={featuresSectionRef} className={"features-section product-page"}>
            <div className={'container'}>
                {title}
                <p className={"desc-features"}>{description}</p>
                <div className={`tabs ${className}`}>
                    <ul className={"tabNav"}>
                        {featuresArray.map((item, index) => {
                            return (
                                <li
                                    className={`tab ${activeTab === index ? 'active' : ''}`}
                                    onClick={() => handleClick(index)}
                                    key={index}
                                >
                                    <p>{item.heading}</p>
                                </li>
                            );
                        })}
                        {tourId == "" ? "" : <li className={"tab tour-icon"}>
                            <Link href={`#${tourId}`}>
                                <p>{t('featuresMonitoring.interactiveProductTour')}</p>
                            </Link>
                        </li>}
                    </ul>
                    <div className={'content'}>
                        {featuresArray.map((section, index) => (
                            <ProductFeatureSection
                                key={index}
                                id={section.id}
                                image={section.image}
                                imageAlt={section.imageAlt}
                                title={section.title}
                                list={section.list}
                                sectionRef={(el: any) => sections.current[index] = el}
                                hasActionsDemo={hasActionsDemo}
                                exploreBtn={section.exploreBtn}
                                exploreLink={section.exploreLink}
                                docsLink={section.docsLink}
                                docsLabel={section.docsLabel}
                            />
                        ))}
                    </div>
                </div>

                {!isTryOut && <div className="singup-product gradient-border-bottom gradient-border-top ">
                    <p>
                        {t('featuresMonitoring.tryItFree')}
                        <Link href={"https://app.middleware.io/auth/register/"} target={"_blank"}
                              className={"read-more"}> {t('featuresMonitoring.signUpNow')} </Link>
                    </p>
                </div>}

            </div>
        </section>
    );
};

export default FeaturesMonitoring;
